import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Boat,
  CalendarBlank,
  CaretRight,
  Lock,
  MagnifyingGlass,
  Shield,
  Wrench,
} from "@phosphor-icons/react";
import { useState, type FormEvent } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { TextField } from "@navi-soft/ui/text-field";
import {
  ForumPageChrome,
  ForumPageShell,
} from "@/components/forum-page-chrome";
import { fetchMe, findHulls, type HullCard } from "@/lib/api";
import { humanizeHint } from "@/lib/humanize-hint";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/find")({
  component: FindPage,
});

function FindPage() {
  const t = useTranslations("app");
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const res = await fetchMe();
      if (!res.ok) throw new Error(res.error);
      return res.data;
    },
  });
  const authenticated = Boolean(meQuery.data?.authenticated);

  const findQuery = useQuery({
    queryKey: ["hulls", submitted],
    enabled: authenticated && submitted.length >= 2,
    queryFn: async () => {
      const res = await findHulls(submitted);
      if (!res.ok) throw new Error(res.error);
      return res.data.items;
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSelectedKey(null);
    setSubmitted(q.trim());
  }

  const showResults = submitted.length >= 2;
  const items = findQuery.data ?? [];
  const selected =
    items.find((h) => hullKey(h) === selectedKey) ?? items[0] ?? null;

  return (
    <ForumPageShell>
      <ForumPageChrome
        eyebrow={t("findEyebrow")}
        title={t("findTitle")}
        intro={t("findIntro")}
      />

      {!authenticated ? (
        <p className="text-ui-dense text-[var(--brand-text-muted)]">
          {t("homeNeedLogin")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] shadow-subtle">
          <form
            onSubmit={onSubmit}
            className="border-b border-[var(--brand-border)] p-4 sm:p-6"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <TextField
                size="md"
                className="w-full min-w-0 flex-1"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("findQueryPlaceholder")}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                leadingAdornment={
                  <MagnifyingGlass className="size-3.5" weight="bold" />
                }
              />
              <Button
                type="submit"
                variant="primary"
                size="md"
                className="shrink-0 sm:min-w-28"
                disabled={q.trim().length < 2}
              >
                {t("findSubmit")}
              </Button>
            </div>
            <p className="mt-2 text-ui-micro text-[var(--brand-text-muted)]">
              {t("findHintLine")}
            </p>
          </form>

          {!showResults ? (
            <div className="px-4 py-10 text-center sm:px-6">
              <MagnifyingGlass
                className="mx-auto mb-3 size-8 text-[var(--brand-border)]"
                weight="light"
              />
              <p className="text-ui-dense text-[var(--brand-text-muted)]">
                {t("findIdle")}
              </p>
            </div>
          ) : findQuery.isLoading ? (
            <div className="px-4 py-10 text-ui-dense text-[var(--brand-text-muted)] sm:px-6">
              {t("loading")}
            </div>
          ) : findQuery.isError ? (
            <div className="px-4 py-10 text-ui-dense text-[var(--brand-text-muted)] sm:px-6">
              {t("findError")}
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center text-ui-dense text-[var(--brand-text-muted)] sm:px-6">
              {t("findEmpty")}
            </div>
          ) : (
            <div className="flex flex-col">
              {items.length > 1 ? (
                <div className="flex gap-1 overflow-x-auto border-b border-[var(--brand-border)] px-3 py-2">
                  {items.map((h) => {
                    const key = hullKey(h);
                    const active = selected
                      ? hullKey(selected) === key
                      : false;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedKey(key)}
                        className={cn(
                          "shrink-0 rounded px-2.5 py-1.5 font-mono text-ui-micro",
                          active
                            ? "bg-[var(--brand-navy)] text-white"
                            : "text-[var(--brand-navy)] hover:bg-[var(--brand-bg-section)]",
                        )}
                      >
                        {h.identifier_value}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {selected ? <HullLookupCard hull={selected} /> : null}
            </div>
          )}
        </div>
      )}
    </ForumPageShell>
  );
}

function hullKey(h: HullCard) {
  return `${h.system_name}:${h.identifier_value}`;
}

function lifecycleLabel(
  raw: string | null | undefined,
  t: ReturnType<typeof useTranslations<"app">>,
): string {
  if (!raw) return t("findLifecycleUnknown");
  if (raw === "in-service" || raw === "in_service") return t("findLifecycleInService");
  return raw;
}

function HullLookupCard({ hull }: { hull: HullCard }) {
  const t = useTranslations("app");
  const navigate = useNavigate();
  const platformLabel =
    hull.platform_label?.trim() ||
    hull.platform_id ||
    t("findPlatformUnknown");
  const brand = hull.brand?.trim() || humanizeHint(hull.system_name);
  const version = hull.platform_version
    ? hull.platform_version.startsWith("v")
      ? hull.platform_version
      : `v${hull.platform_version}`
    : null;

  const catalogSearch =
    hull.platform_id && hull.system_name
      ? {
          yard: hull.system_name,
          platform: hull.platform_id,
          win: hull.identifier_value,
        }
      : { win: hull.identifier_value };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <Boat
                  className="size-[18px] text-[var(--brand-navy)]"
                  weight="bold"
                />
                <span className="font-heading text-lg font-bold text-[var(--brand-navy)]">
                  {platformLabel}
                </span>
                {version ? (
                  <span className="rounded bg-[var(--brand-bg-section)] px-2 py-0.5 font-mono text-ui-micro text-[var(--brand-text-muted)]">
                    {version}
                  </span>
                ) : null}
              </div>
              <p className="text-ui-dense text-[var(--brand-text-muted)]">
                {brand}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-ui-micro font-medium text-emerald-700">
              {lifecycleLabel(hull.lifecycle, t)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-ui-micro sm:grid-cols-3">
            <div className="rounded-lg bg-[var(--brand-bg-section)] p-3">
              <span className="mb-0.5 block text-[var(--brand-text-muted)]">
                {t("findFieldWin")}
              </span>
              <span className="font-mono font-medium text-[var(--brand-navy)]">
                {hull.identifier_value}
              </span>
            </div>
            <div className="rounded-lg bg-[var(--brand-bg-section)] p-3">
              <span className="mb-0.5 block text-[var(--brand-text-muted)]">
                {t("findHullNo")}
              </span>
              <span className="font-mono font-medium text-[var(--brand-navy)]">
                {hull.hull_number || "—"}
              </span>
            </div>
            <div className="rounded-lg bg-[var(--brand-bg-section)] p-3">
              <span className="mb-0.5 block text-[var(--brand-text-muted)]">
                {t("findCategory")}
              </span>
              <span className="font-semibold text-[var(--brand-navy)]">
                {hull.design_category
                  ? `RCD ${hull.design_category}`
                  : "—"}
              </span>
            </div>
          </div>

          {hull.build_sheet.length > 0 ? (
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-ui-micro font-semibold text-[var(--brand-navy)]">
                <CaretRight className="size-3" weight="bold" />
                {t("findBuildSheet")}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {hull.build_sheet.map((item) => (
                  <span
                    key={item}
                    className="rounded bg-[var(--brand-bg-section)] px-2 py-1 text-ui-micro text-[var(--brand-navy)]"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {hull.config_frozen_at ? (
            <div className="flex items-center gap-2 text-ui-micro text-[var(--brand-text-muted)]">
              <CalendarBlank className="size-3" />
              {t("findConfigFrozen")}: {hull.config_frozen_at}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="primary"
              size="md"
              className="shrink-0"
              onClick={() => {
                void navigate({ to: "/catalog", search: catalogSearch });
              }}
            >
              {t("findOpenCatalog")}
            </Button>
            {hull.platform_id && hull.system_name ? (
              <Button
                type="button"
                variant="secondary"
                size="md"
                className="shrink-0"
                onClick={() => {
                  void navigate({
                    to: "/catalog",
                    search: {
                      yard: hull.system_name!,
                      platform: hull.platform_id!,
                    },
                  });
                }}
              >
                {t("findOpenPlatform")}
              </Button>
            ) : null}
          </div>
        </div>

        <aside className="space-y-3 lg:w-72">
          <h4 className="flex items-center gap-1.5 text-ui-micro font-semibold text-[var(--brand-navy)]">
            <Wrench className="size-3" weight="bold" />
            {t("findServiceTitle")}
          </h4>

          <div className="space-y-2 rounded-lg border border-[var(--brand-border)] p-3 text-ui-micro">
            <div className="flex justify-between gap-2">
              <span className="text-[var(--brand-text-muted)]">
                {t("findLastService")}
              </span>
              <span className="font-medium text-[var(--brand-navy)]">
                {hull.last_service_at || "—"}
              </span>
            </div>
            <p className="text-[var(--brand-text-muted)]">
              {hull.last_service_label === "scheduled-service"
                ? t("findScheduledService")
                : hull.last_service_label || t("findServiceLater")}
            </p>
          </div>

          {hull.replacements.map((r) => (
            <div
              key={`${r.part_number}-${r.date}`}
              className="rounded-lg border border-[var(--brand-border)] p-3 text-ui-micro"
            >
              <div className="mb-1 flex justify-between gap-2">
                <span className="font-medium text-[var(--brand-navy)]">
                  {r.label}
                </span>
                <span className="text-[var(--brand-text-muted)]">{r.date}</span>
              </div>
              <span className="font-mono text-[var(--brand-text-muted)]">
                {r.part_number}
              </span>
            </div>
          ))}

          {hull.campaign ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-ui-micro text-amber-800">
              <Shield
                className="mt-0.5 size-3.5 shrink-0 text-amber-600"
                weight="fill"
              />
              <span>{hull.campaign}</span>
            </div>
          ) : null}

          <div className="flex items-center gap-1.5 text-ui-micro text-[var(--brand-text-muted)] opacity-60">
            <Lock className="size-3" />
            {t("findFieldsHidden")}
          </div>
        </aside>
      </div>
    </div>
  );
}
